import { Controller } from "@hotwired/stimulus"
import { cameraHasOpened } from "../camera_nfc_conflict"
import { nfcPermissionGranted } from "../nfc_permission"

// Chrome for Android only — Web NFC (NDEFReader) is not available elsewhere.
// Android hands a tag to its own dispatcher unless something in the foreground
// holds reader mode, and a blank NTAG dispatches to the system's "tag vazia"
// screen. Chrome holds reader mode only while an NDEFReader.scan() is armed on
// the visible page, so every operator screen without one is a screen where
// resting a card on the phone throws the operator out of the app.
//
// This is the reader for the screens that have none: it arms, and that is the
// whole job. Readings are swallowed — nothing here knows what a card would mean
// on an arbitrary screen, and dispatching nfc:read into a document that never
// asked for it would feed code fields the operator is not filling in.
//
// It never prompts: the permission prompt only opens under a user gesture, and
// this controller has no UI to tap. Until the origin is granted — which happens
// the first time an operator taps "Ler chip NFC" on a screen that reads cards for
// real — Android keeps taking the tag, same as today.
//
// Deliberately silent about every failure too. The operator asked for nothing
// here; a message about a reader they did not know existed is noise.
export default class extends Controller {
  #abortController = null
  #onCameraOpened = () => this.#disarm()

  async connect() {
    if (!("NDEFReader" in window)) return
    if (this.#unavailable()) return

    window.addEventListener("camera:opened", this.#onCameraOpened)

    if (!(await nfcPermissionGranted())) return

    // Re-checked after the await: the camera can open, and Turbo can settle a new
    // body carrying its own reader, while the permission query is in flight.
    if (this.#unavailable()) return

    this.#arm()
  }

  disconnect() {
    window.removeEventListener("camera:opened", this.#onCameraOpened)
    this.#disarm()
  }

  // private

  // Two reasons to stay out of the way: a camera has poisoned Web NFC for this
  // document's lifetime (see camera_nfc_conflict), or the screen carries its own
  // nfc-reader, which owns the dispatch and does something useful with it. Two
  // scans in one document is a conflict nobody has measured — this one yields.
  #unavailable() {
    return cameraHasOpened() || document.querySelector('[data-controller~="nfc-reader"]') !== null
  }

  #arm() {
    this.#abortController = new AbortController()

    new NDEFReader()
      .scan({ signal: this.#abortController.signal })
      .catch(() => this.#disarm())
  }

  #disarm() {
    this.#abortController?.abort()
    this.#abortController = null
  }
}
